import React from 'react'
import moment from 'moment'
import styles from './StatsWidget.scss'
import smartDate from '@qrohlf/smart-date'
import { Distance, Elevation, Duration } from 'common/units'
import { UnderlineSelector } from 'common/UnderlineSelector'
import { DashedMetric } from 'common/DashedMetric'
import { Card } from 'common/styleguide/Card'
import { Icon } from 'common/Icon'
import { capitalize, getPreferences } from 'lib/utils'
import fetchJSON from 'lib/fetchJSON'
import { Msg, commonMetrics } from 'lib/localization'
import { defineMessages } from 'react-intl'
import { Map } from 'immutable'

const messages = defineMessages({
  traveled: {
    id: 'statsWidget.traveled',
    defaultMessage: '{units} Traveled'
  },
  elevationGained: {
    id: 'statsWidget.elevationGained',
    defaultMessage: '{units} of Elevation Gained'
  },
  ridesLogged: {
    id: 'statsWidget.ridesLogged',
    defaultMessage: '{count, plural, =0 {Rides Logged} one {Ride Logged} other {Rides Logged}}'
  },
  photosTaken: {
    id: 'statsWidget.photosTaken',
    defaultMessage: '{count, plural, =0 {Photos Taken} one {Photo Taken} other {Photos Taken}}'
  },
  week: {
    id: 'statsWidget.week',
    defaultMessage: 'Week',
    description: 'Seven days'
  },
  month: {
    id: 'statsWidget.month',
    defaultMessage: 'Month'
  },
  year: {
    id: 'statsWidget.year',
    defaultMessage: 'Year'
  },
  career: {
    id: 'statsWidget.career',
    defaultMessage: 'Career'
  }
})

export const getCareerStats = (userId, interval, value) => (
  fetchJSON(`/users/${userId}/career_stats.json?apikey=x&interval=${interval}&interval_value=${value}`)
)

export class StatsWidget extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      interval: props.defaultInterval || (getPreferences().default_career_interval ?? 'career'),
      intervalIndex: 0,
      loading: true,
      stats: props.initialStats || {}
    }
  }

  componentDidMount () {
    const { initialStats } = this.props
    if (!initialStats) {
      this.refreshStats()
    }
  }

  setInterval = (interval) => {
    // TODO - use currentUser setPreference action dispatch instead
    // of keeping this value in state
    this.setState({ interval, intervalIndex: 0, loading: true }, this.refreshStats)
    if (this.props.persistInterval) {
      this.props.persistInterval(interval)
    }
  }

  formatInterval = () => {
    const { interval, intervalIndex } = this.state
    switch (interval) {
      case 'month':
        const month = moment().subtract(intervalIndex, 'month')
        return month.year() === moment().year()
          ? month.format('MMMM')
          : month.format('MMMM YYYY')
      case 'year':
        return '' + moment().subtract(intervalIndex, 'year').year()
      case 'week': {
        const start = moment().startOf('week').subtract(intervalIndex, 'week')
        const end = start.clone().add(6, 'days')
        return smartDate([start.toISOString(), end.toISOString()], { includeTime: false })
      }
      case 'career':
        return <Msg m={messages.career} />
      default:
        return `unknown interval: ${interval}`
    }
  }

  static getIntervalValue = (interval, intervalIndex) => {
    switch (interval) {
      case 'month':
        return moment().subtract(intervalIndex, 'month').format('YYYY-MM')
      case 'year':
        return '' + moment().subtract(intervalIndex, 'year').year()
      case 'week': {
        return '' + moment().startOf('week').subtract(intervalIndex, 'week').format('YYYY-MM-DD')
      }
      case 'career':
        return 'career'
    }
  }

  refreshStats = () => {
    const { interval, intervalIndex } = this.state
    const { user } = this.props
    getCareerStats(user.id, interval, StatsWidget.getIntervalValue(interval, intervalIndex))
      .then(stats => this.setState({ stats }))
  }

  increment = () => {
    const { intervalIndex } = this.state
    this.setState({ intervalIndex: intervalIndex + 1, loading: true }, this.refreshStats)
  }

  decrement = () => {
    const { intervalIndex } = this.state
    this.setState({ intervalIndex: Math.max(intervalIndex - 1, 0), loading: true }, this.refreshStats)
  }

  render () {
    const { interval, intervalIndex, stats } = this.state

    return (
      <Card noPadding>
        <div className={styles.pager}>
          <span>{interval !== 'career' && <a onClick={this.increment} className={styles.arrow}><Icon i='arrow-back' size={32} /></a>}</span>
          <span>{this.formatInterval()}</span>
          <span>
            {interval !== 'career' &&
              (intervalIndex !== 0
                ? <a onClick={this.decrement} className={styles.arrow}><Icon i='arrow-forward' size={32} /></a>
                : <Icon i='arrow-forward-disabled' size={32} />)}
          </span>
        </div>
        <div className={styles.AtAGlance} style={{ textAlign: 'center' }}>
          <Distance value={stats.distance}>
            {m => <DashedMetric className={styles.statMetric}
              label={<Msg m={messages.traveled} values={{ units: capitalize(m.long) }} />}
              value={m.valueToString()} />}
          </Distance>
          <Elevation value={stats.elevation_gain}>
            {m => <DashedMetric className={styles.statMetric}
              label={<Msg m={messages.elevationGained} values={{ units: capitalize(m.long) }} />}
              value={m.valueToString()} />}
          </Elevation>
          <DashedMetric className={styles.statMetric}
            label={<Msg m={messages.ridesLogged} values={{ count: stats.trips }} />}
            value={stats.trips && stats.trips.toLocaleString('en-US')} />
          <Duration value={stats.moving_time} bigUnits>
            {m => <DashedMetric className={styles.statMetric}
              label={<Msg startCase m={commonMetrics.hoursRidden} values={{ count: stats.moving_time > 3600 ? 0 : 1 }} />}
              value={m.valueToString()} />}
          </Duration>
          <DashedMetric className={styles.statMetric}
            label={<Msg startCase m={commonMetrics.caloriesBurned} />}
            value={stats.calories_burned && stats.calories_burned.toLocaleString('en-US')} />
          <DashedMetric className={styles.statMetric}
            label={<Msg m={messages.photosTaken} values={{ count: stats.photos_uploaded }} />}
            value={stats.photos_uploaded && stats.photos_uploaded.toLocaleString('en-US')} />
        </div>
        <UnderlineSelector
          className={styles.headingFilter}
          styles={{ option: styles.option, selected: styles.selected }}
          options={Map({ week: <Msg m={messages.week} />, month: <Msg m={messages.month} />, year: <Msg m={messages.year} />, career: <Msg m={messages.career} /> })}
          selected={interval}
          onSelectionChanged={this.setInterval} />
      </Card>
    )
  }
}
